import React, { useState, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, FormHelperText, CircularProgress, useTheme, OutlinedInput, TextField } from "@mui/material";
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import { setReturning as updateReturning, reset } from '../../redux/userSlice';
import { reset as insuranceReset } from '../../redux/insuranceSlice';
import { reset as stripeReset } from '../../redux/stripeSlice';
import { reset as portalReset } from '../../redux/portalSlice';
import MoveButton from "../../components/MoveButton";
import Question from "../../components/Question";
import { useAuth } from "../../hooks/useAuth";
import ContinueButton from '../../components/ContinueButton';
import { continueButtonSx, phoneInputSx, textFieldSx } from '../../lib/uiformatting';
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

const CustomMuiOutlinedInput = forwardRef((props, ref) => {
    return (
        <OutlinedInput
            {...props}
            inputRef={ref}
            label=""
            notched={false}
            sx={{ ...phoneInputSx }}
        />
    );
});

const ReturningLogin = () => {
    const theme = useTheme();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, handleChange, phoneNoValid, sendOtp, showOtpInput, isSendingOtp, isVerifyOtpErr, setShowOtpInput } = useAuth();
    const [firstEnter, setFirstEnter] = useState(true);
    const [phoneNo, setPhoneNo] = useState();

    useEffect(() => {
        setPhoneNo('');
    }, []);

    useEffect(() => {
        dispatch(reset());
        dispatch(insuranceReset());
        dispatch(stripeReset());
        dispatch(portalReset());
        setPhoneNo('');
        dispatch(updateReturning(true));
    }, [dispatch]);

    const handlePhoneChange = (value) => {
        setPhoneNo(value || '');
        handleChange("phoneNo", value);
    };

    return (
        <Question
            title="Welcome back to Piction Health."
            subtitle="What's your mobile number? We'll text you a code to confirm it's you."
            noBack={true}
            returning={true}
            input={
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.default
                }}>
                    <Box sx={{ width: '100%', maxWidth: '600px' }}>
                        <PhoneInput
                            country="US"
                            placeholder="(234) 567-8901"
                            value={phoneNo}
                            onChange={handlePhoneChange}
                            onBlur={() => setFirstEnter(false)}
                            inputComponent={CustomMuiOutlinedInput}
                        />
                        <FormHelperText
                            error={!phoneNoValid && !firstEnter}
                            sx={{
                                ml: 0,
                                mt: 0.5,
                            }}
                        >
                            {!phoneNoValid && !firstEnter
                                ? "Please enter a valid 10-digit US phone number"
                                : ""}
                        </FormHelperText>

                        {showOtpInput ? (
                            <>
                                <TextField
                                    placeholder="Enter code"
                                    type="tel"
                                    inputMode="decimal"
                                    sx={{ ...textFieldSx, width: "100%", mt: 2 }}
                                    autoComplete="off"
                                    value={user?.otpCode}
                                    onChange={(e) => handleChange("otpCode", e.target.value)}
                                    disabled={user?.otpCode?.length >= 6}
                                />
                                {isVerifyOtpErr && (
                                    <Box sx={{ pl: "60%", mt: 2 }}>
                                        <MoveButton
                                            sx={{ ml: "auto" }}
                                            onClick={() => {
                                                setPhoneNo('');
                                                setShowOtpInput(false);
                                                setPageAndDeferNavigate(dispatch, '/returning', navigate);
                                            }}
                                            title="Try Again"
                                        />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ ...continueButtonSx, width: '100%', zIndex: 9999, mt: 4 }}>
                                <ContinueButton
                                    title={isSendingOtp ? <CircularProgress color="secondary" /> : "Get Code"}
                                    disabled={!phoneNoValid || !phoneNo}
                                    onClick={sendOtp}
                                    selected={phoneNoValid && phoneNo}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            }
        />
    );
};

export default ReturningLogin;
